import { Extension } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'

import { insertToggle } from '@/components/shared/notion-toggle'
import { NOTION_BLOCK_SHORTCUTS, type NotionBlockShortcutId } from '@/lib/notion-shortcuts'

/**
 * Notion 互換の Cmd/Ctrl + Option(Alt) + 数字/- ショートカット（割り当ては NOTION_BLOCK_SHORTCUTS）。
 *
 * macOS では Option(Alt) + 数字 が特殊文字（例: Option+7 → "¶"）を生成し、
 * Tiptap の文字列キーバインド（mousetrap 形式）では取りこぼすことがある。
 * そのため `event.code` で判定する ProseMirror プラグインとして実装する。
 */
type KeymapAction = () => boolean

export const NotionKeymap = Extension.create({
  name: 'notionKeymap',

  addProseMirrorPlugins() {
    const editor = this.editor

    const actions: Record<NotionBlockShortcutId, KeymapAction> = {
      paragraph: () => editor.chain().focus().setParagraph().run(),
      heading1: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      heading2: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      heading3: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      taskList: () => editor.chain().focus().toggleTaskList().run(),
      bulletList: () => editor.chain().focus().toggleBulletList().run(),
      orderedList: () => editor.chain().focus().toggleOrderedList().run(),
      toggle: () => insertToggle(editor),
      codeBlock: () => editor.chain().focus().toggleCodeBlock().run(),
      blockquote: () => editor.chain().focus().toggleBlockquote().run(),
      callout: () => editor.chain().focus().toggleCallout().run(),
    }
    const actionsByCode = new Map(
      (Object.keys(actions) as NotionBlockShortcutId[]).map((id) => [NOTION_BLOCK_SHORTCUTS[id].code, actions[id]]),
    )

    return [
      new Plugin({
        props: {
          handleKeyDown: (_view, event) => {
            if (!event.altKey) return false
            if (!(event.metaKey || event.ctrlKey)) return false
            if (event.shiftKey) return false

            const action = actionsByCode.get(event.code)
            if (!action) return false

            event.preventDefault()
            return action()
          },
        },
      }),
    ]
  },
})
